/**
 * VoiceInputModal.jsx
 * Voice input for food tracking - like Kundenportal NutritionTracking
 */
import React, { useState, useRef, useEffect } from 'react';
import { Mic, MicOff, Loader2, Check, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MEAL_TYPES = [
  { key: 'breakfast', label: 'Frühstück', emoji: '🌅' },
  { key: 'lunch', label: 'Mittag', emoji: '☀️' },
  { key: 'dinner', label: 'Abend', emoji: '🌙' },
  { key: 'snacks', label: 'Snack', emoji: '🍪' },
];

const VoiceInputModal = ({ isOpen, onClose, portalToken, onFoodAdded }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [transcription, setTranscription] = useState('');
  const [detectedFoods, setDetectedFoods] = useState([]);
  const [totals, setTotals] = useState({ calories: 0, protein: 0, carbs: 0, fat: 0 });
  const [step, setStep] = useState('record'); // 'record', 'confirm'
  const [recordingTime, setRecordingTime] = useState(0);
  const [selectedMealType, setSelectedMealType] = useState('lunch');
  const [selectedDate] = useState(new Date().toISOString().split('T')[0]);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
    };
  }, []);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };
      
      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        processRecording(audioBlob);
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
    } catch (error) {
      console.error('Error starting recording:', error);
      toast.error('Mikrofon-Zugriff verweigert');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
  };

  const processRecording = async (audioBlob) => {
    setIsProcessing(true);
    setStep('confirm');
    
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');
      
      // Step 1: Transcribe
      const transcribeResponse = await axios.post(
        `${API}/nutrition/voice/transcribe`,
        formData,
        { headers: { Authorization: `Bearer ${portalToken}` } }
      );
      
      const text = transcribeResponse.data.transcription;
      setTranscription(text);
      
      // Step 2: Parse food
      const parseResponse = await axios.post(
        `${API}/nutrition/voice/parse`,
        { text },
        { headers: { Authorization: `Bearer ${portalToken}` } }
      );
      
      const foods = parseResponse.data.foods || [];
      setDetectedFoods(foods);
      
      // Calculate totals
      const newTotals = foods.reduce((acc, food) => ({
        calories: acc.calories + (food.calories || 0),
        protein: acc.protein + (food.protein || 0),
        carbs: acc.carbs + (food.carbs || 0),
        fat: acc.fat + (food.fat || 0)
      }), { calories: 0, protein: 0, carbs: 0, fat: 0 });
      setTotals(newTotals);
      
    } catch (error) {
      console.error('Error processing voice:', error);
      toast.error('Fehler bei der Sprachverarbeitung');
      setStep('record');
    } finally {
      setIsProcessing(false);
    }
  };

  const confirmEntry = async () => {
    try {
      for (const food of detectedFoods) {
        await axios.post(
          `${API}/portal/nutrition/entries`,
          {
            date: selectedDate,
            meal_type: selectedMealType,
            food_name: food.name,
            portion_grams: food.portion_grams || 100,
            calories: food.calories || 0,
            protein: food.protein || 0,
            carbs: food.carbs || 0,
            fat: food.fat || 0
          },
          { headers: { Authorization: `Bearer ${portalToken}` } }
        );
      }
      toast.success(`${detectedFoods.length} Einträge hinzugefügt!`);
      if (onFoodAdded) onFoodAdded();
      handleClose();
    } catch (error) {
      console.error('Error saving voice entry:', error);
      toast.error('Fehler beim Speichern');
    }
  };

  const handleClose = () => {
    setStep('record');
    setTranscription('');
    setDetectedFoods([]);
    setTotals({ calories: 0, protein: 0, carbs: 0, fat: 0 });
    setRecordingTime(0);
    setIsRecording(false);
    setIsProcessing(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-zinc-900 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-white font-bold text-lg">🎤 Spracheingabe</h3>
          <button 
            onClick={handleClose}
            className="text-zinc-400 text-2xl hover:text-white"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        {step === 'record' && (
          <div className="text-center space-y-6">
            <p className="text-zinc-400 text-sm">
              Sag einfach, was du gegessen hast. Z.B.:<br/>
              <span className="text-zinc-300">"200 Gramm Hühnerbrust mit Reis und Brokkoli"</span>
            </p>
            
            {/* Recording Button */}
            <button
              onClick={isRecording ? stopRecording : startRecording}
              className={`w-24 h-24 rounded-full mx-auto flex items-center justify-center transition-all ${
                isRecording 
                  ? 'bg-red-500 animate-pulse' 
                  : 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600'
              }`}
            >
              {isRecording ? (
                <MicOff className="w-10 h-10 text-white" />
              ) : (
                <Mic className="w-10 h-10 text-white" />
              )}
            </button>
            
            {isRecording && (
              <p className="text-red-400 font-medium">
                Aufnahme läuft... {recordingTime}s
              </p>
            )}
            
            <p className="text-zinc-500 text-xs">
              {isRecording ? 'Tippe zum Stoppen' : 'Tippe zum Starten'}
            </p>
          </div>
        )}
        
        {step === 'confirm' && (
          <div className="space-y-4">
            {isProcessing ? (
              <div className="text-center py-8">
                <Loader2 className="w-12 h-12 text-yellow-400 animate-spin mx-auto mb-4" />
                <p className="text-zinc-400">Verarbeite Spracheingabe...</p>
              </div>
            ) : (
              <>
                {/* Transcription */}
                <div className="bg-zinc-800 rounded-xl p-4">
                  <p className="text-zinc-400 text-xs mb-1">Du hast gesagt:</p>
                  <p className="text-white">{transcription}</p>
                </div>
                
                {/* Detected Foods */}
                {detectedFoods.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-zinc-400 text-sm">Erkannte Lebensmittel:</p>
                    {detectedFoods.map((food, idx) => (
                      <div key={idx} className="bg-zinc-800 rounded-xl p-3 flex justify-between items-center">
                        <div>
                          <p className="text-white font-medium">{food.name}</p>
                          <p className="text-zinc-500 text-xs">{food.portion_grams || 100}g</p>
                        </div>
                        <div className="text-right">
                          <p className="text-yellow-400 font-bold">{Math.round(food.calories || 0)} kcal</p>
                          <p className="text-zinc-500 text-xs">
                            P:{food.protein || 0}g K:{food.carbs || 0}g F:{food.fat || 0}g
                          </p>
                        </div>
                      </div>
                    ))}
                    
                    {/* Totals */}
                    <div className="bg-green-500/20 border border-green-500/30 rounded-xl p-3 mt-2">
                      <div className="flex justify-between">
                        <span className="text-green-400 font-medium">Gesamt:</span>
                        <span className="text-white font-bold">{Math.round(totals.calories || 0)} kcal</span>
                      </div>
                    </div>
                  </div>
                )}
                
                {detectedFoods.length === 0 && (
                  <div className="text-center py-4">
                    <p className="text-zinc-400">Keine Lebensmittel erkannt. Bitte versuche es erneut.</p>
                  </div>
                )}
                
                {/* Meal Type Selection */}
                <div>
                  <p className="text-zinc-400 text-sm mb-2">Mahlzeit:</p>
                  <div className="grid grid-cols-4 gap-2">
                    {MEAL_TYPES.map(meal => (
                      <button
                        key={meal.key}
                        onClick={() => setSelectedMealType(meal.key)}
                        className={`p-2 rounded-lg text-center transition-all ${
                          selectedMealType === meal.key
                            ? 'bg-yellow-500 text-black'
                            : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'
                        }`}
                      >
                        <span className="text-lg">{meal.emoji}</span>
                        <p className="text-xs mt-1">{meal.label}</p>
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Action Buttons */}
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => setStep('record')}
                    className="flex-1 py-3 bg-zinc-800 text-zinc-300 rounded-xl font-medium hover:bg-zinc-700"
                  >
                    Nochmal
                  </button>
                  <button
                    onClick={confirmEntry}
                    disabled={detectedFoods.length === 0}
                    className="flex-1 py-3 bg-green-500 text-white rounded-xl font-medium hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    <Check className="w-5 h-5" />
                    Speichern
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default VoiceInputModal;
